import gamTimeouts from '../../consts/config/gamTimeouts';
import uamTimeouts from '../../consts/config/uamTimeouts';
import * as gamMethods from '../../consts/gam/gamMethods';
import * as uamMethods from '../../consts/uam/uamMethods';
import * as yapActionStatusTypes from '../../consts/yap/yapActionStatusTypes';
import * as yapConnectionStatusTypes from '../../consts/yap/yapConnectionStatusTypes';
import * as yapFlowTypes from '../../consts/yap/yapFlowTypes';
import DEVICE_TYPES from '../../enums/device/deviceTypes';
import {setScpCheckInProgress} from '../../state/ducks/global';
import {
    clearIotDevice,
    clearIotDeviceData,
    clearIotDeviceHolderData,
    setIsDeviceNotSupported,
    setIsWrongDevice,
    updateIotDeviceData,
} from '../../state/ducks/iotDevice/actions';
import {
    setDeviceInfoReceived,
    setHolderInfoReceived,
    setOfflineYapFirmwareInProgress,
    setSyncLastRequestId,
    setYapDeviceConnected,
    setYapHolderConnected,
    setYapSyncInProgress,
} from '../../state/ducks/yapEncrypted';
import {makeSelectConsumerProduct, makeSelectIotDeviceFullData} from '../../state/selectors/consumer';
import {
    makeSelectIotDeviceData,
    makeSelectIsDeviceNotSupported,
    selectIotDeviceType,
    selectIotHolderType,
} from '../../state/selectors/iotDevice';
import {selectMwDeviceInternalId} from '../../state/selectors/mwDevice';
import {
    makeSelectAssetByType,
    makeSelectAssetIdByType,
    makeSelectIsDeviceSyncInProgress,
    makeSelectIsHolderByAssetId,
    makeSelectIsOfflineYapFirmwareInProgress,
    makeSelectIsYapDeviceInfoReceived,
    makeSelectIsYapHolderInfoReceived,
} from '../../state/selectors/yapEncrypted';
import {dispatch, getState} from '../../state/store';
import backendService from '../app/backendService';
import cmClientService from '../communicationLayer/cmClientService';
import cmService from '../communicationLayer/cmService';
import connectivityService from '../device/connectivityService';
import devicePreSyncPingScheduler from '../device/devicePreSyncPingScheduler';
import GamClient from '../gam/gamClient';
import gamClientService from '../gam/gamClientService';
import iotDeviceService from '../iotDevice/iotDeviceService';
import log from '../logger/log';
import marketConfigService from '../marketConfigService';
import pairingService from '../pairingService';
import scpService from '../scp/scpService';
import UamClient from '../uam/uamClient';
import uamClientService from '../uam/uamClientService';
import yapWebSocketService from '../yap/yapWebSocketService';
import yapFlowTimeoutService from './yapFlowTimeoutService';
import yapService from './yapService';
import yapStateService from './yapStateService';

const initScpData = async ({isReconnect, isHolderOnly, initialDeviceType, readHolderSerialNumberTimeoutMs}) => {
    const deviceType = selectIotDeviceType(getState());
    const holderType = selectIotHolderType(getState());

    if (isReconnect) {
        dispatch(clearIotDeviceData());
    }

    if (!pairingService.isDeviceConnected()) return {};

    dispatch(setScpCheckInProgress(true));

    const onError = (isHolder) => {
        if (isHolder) {
            dispatch(clearIotDeviceHolderData());
            return {};
        }

        dispatch(clearIotDevice());

        if (isHolder === undefined) {
            dispatch(setScpCheckInProgress(false));
        }
        return {};
    };

    const scpReadResult = await scpService.identifyDevice({isHolderOnly, readHolderSerialNumberTimeoutMs});

    if (!scpReadResult) return onError();

    devicePreSyncPingScheduler.startPing();

    const {holder, charger} = scpReadResult;

    if (!charger) return onError();

    const initDeviceData = (scpData, isHolder = false) => {
        if (!scpData) return onError(isHolder);

        const {serial, type} = scpData;
        let codentify = scpData.codentify;

        if (!type && !serial) return onError(isHolder);

        const state = getState();
        const deviceId = selectMwDeviceInternalId(state);

        if (isHolderOnly && !deviceId) return onError(isHolder);

        if (!codentify) {
            const consumerProduct = makeSelectConsumerProduct({deviceSerialNumber: serial})(state);

            codentify = consumerProduct?.codentifyID;
        }

        const getPreparedDeviceType = () => {
            const isInitialConnectOfOriginalDevice =
                initialDeviceType === DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_CHARGER &&
                (type === DEVICE_TYPES.DEV_TYPE_P1V31_CHARGER || type === DEVICE_TYPES.DEV_TYPE_P1V31_HOLDER);
            const isReconnectOfOriginalDevice =
                isReconnect &&
                (deviceType === DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_CHARGER ||
                    holderType === DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_HOLDER);

            if (isInitialConnectOfOriginalDevice || isReconnectOfOriginalDevice) {
                return isHolder
                    ? DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_HOLDER
                    : DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_CHARGER;
            }

            return type;
        };

        if (isHolder && serial) {
            const holderAsset = makeSelectAssetByType(isHolder)(state);
            const {serialNumber} = holderAsset || {};

            if (serialNumber && serialNumber !== serial) {
                //clear all data about previous holder if new one is connected
                pairingService.disconnectAndClearHolderState();
            }
        }

        dispatch(
            updateIotDeviceData({
                [isHolder ? 'holder' : 'device']: {
                    deviceSerialNumber: serial,
                    codentify,
                    type: getPreparedDeviceType(),
                },
            })
        );

        if (isHolder) {
            const device = {isHolderConnected: true, holder_connected: true};
            const holderData = {holder_connected: true};

            dispatch(updateIotDeviceData({device, holder: holderData}));
        }

        return {serial, codentify, type};
    };

    const {serial: holderSerial, codentify: holderCodentify} = initDeviceData(holder, true);
    const {serial, codentify, type} = isHolderOnly ? {} : initDeviceData(charger);

    dispatch(setScpCheckInProgress(false));

    return {serial, codentify, holderSerial, holderCodentify, type};
};

const syncAndSubscribeOnDevice = async (isHolder) => {
    const {device} = makeSelectIotDeviceData()(getState());
    const {isHolderConnected} = device || {};

    if (isHolder && !isHolderConnected) {
        log.info('yapConnectionService: syncAndSubscribeOnDevice is ignored for holder. Holder is not connected');
        return;
    }

    const isOfflineMode = makeSelectIsOfflineYapFirmwareInProgress()(getState());
    const isDeviceSyncInProgress = makeSelectIsDeviceSyncInProgress()(getState());

    if (isOfflineMode) {
        dispatch(setOfflineYapFirmwareInProgress(false));
        cmClientService.disconnectCmClient(false);
        cmClientService.disconnectCmClient(true);
    }

    if (isHolder && !isDeviceSyncInProgress) {
        //reconnect charger if holder connect
        await syncAndSubscribeOnDevice();
    }

    await yapWebSocketService.initWebSocket(isHolder);

    await cmClientService.initCmClient(isHolder);
};

const initSynchronizeFlow = (isHolder) => {
    const state = getState();
    const {device} = makeSelectIotDeviceFullData()(state);

    if (!device) return false;

    if (!pairingService.isDeviceConnected()) return false;

    //don't use await to run parallel flows for charger and holder
    synchronizeDevice(isHolder);
    return true;
};

const synchronizeDevice = async (isHolder) => {
    const isGamBackend = backendService.isGamBackend();
    const isDeviceSyncInProgress = makeSelectIsDeviceSyncInProgress(isHolder)(getState());

    if (isDeviceSyncInProgress) return;

    yapFlowTimeoutService.clearFlowTimeout(yapFlowTypes.SYNCHRONIZE, isHolder);
    dispatch(setYapSyncInProgress(true, isHolder));

    const onError = () => {
        dispatch(setYapSyncInProgress(false, isHolder));
    };

    try {
        const assetId = await yapService.getSyncAsset(isHolder);

        if (!assetId) return onError();

        const isDeviceSupported = marketConfigService.isDeviceSupportedForceCheck(isHolder);

        if (!isDeviceSupported) {
            dispatch(setIsDeviceNotSupported());
            return onError();
        }

        const isDeviceNotSupported = makeSelectIsDeviceNotSupported()(getState());

        if (isDeviceNotSupported) {
            return onError();
        }

        //forced reconnect to CM before sync because of 500 error code for sync (busy for new flow)
        if (isHolder) {
            dispatch(setYapHolderConnected(false));
        } else {
            dispatch(setYapDeviceConnected(false));
        }

        await syncAndSubscribeOnDevice(isHolder);
        const isConnected = await cmService.waitUntilConnected({isHolder});

        if (!isConnected) return onError();

        const {isHolderSbl, isSblMode} = iotDeviceService.checkIfDeviceIsInSblModeStatic();

        if (isSblMode) {
            const asset = makeSelectAssetByType(isHolder)(getState());

            if (asset) {
                //update image if device in sbl
                yapStateService.updateAssetMedia([asset]);
            }

            if (isHolder && isHolderSbl) {
                dispatch(setHolderInfoReceived(true));
                dispatch(setYapSyncInProgress(false, isHolder));
                return;
            } else if (!isHolder && !isHolderSbl) {
                dispatch(setDeviceInfoReceived(true));
                dispatch(setYapSyncInProgress(false, isHolder));
                return;
            }
        }

        if (!pairingService.isDeviceConnected()) return onError();

        log.info(`Start synchronization process. isHolder: ${!!isHolder}`);
        const {requestId} =
            (isGamBackend
                ? await new GamClient().callPost({
                      methodName: gamMethods.SYNCHRONIZE_DATA,
                      requestConfig: {args: assetId},
                      isNeedRedirectForOtherErrors: false,
                  })
                : await new UamClient().callPost({
                      methodName: uamMethods.SYNCHRONIZE_DEVICE,
                      requestConfig: {args: assetId},
                      isNeedRedirectForOtherErrors: false,
                  })) || {};

        if (!requestId) {
            log.info('End synchronization process. Request id is not defined.');
            return onError();
        }

        dispatch(setSyncLastRequestId(requestId, isHolder));

        yapFlowTimeoutService.createFlowTimeout({
            type: yapFlowTypes.SYNCHRONIZE,
            timeoutInSec: isGamBackend ? gamTimeouts.GAM_SYNC_TIMEOUT : uamTimeouts.UAM_SYNC_TIMEOUT,
            isHolder,
        });
    } catch (e) {
        log.error(`synchronizeDevice failed. error: ${e}`);
        onError();
    }
};

const proceedRefreshInfo = async (status, assetId) => {
    const isHolder = makeSelectIsHolderByAssetId(assetId)(getState());
    let isSuccess = false;

    const onReturn = () => {
        if (isSuccess) {
            log.info('End synchronization process. Success.');
        } else {
            log.info('End synchronization process. Failed.');
        }

        dispatch(setYapSyncInProgress(false, isHolder));
    };

    if (yapActionStatusTypes.SUCCESS === status) {
        let isDeviceConnected = pairingService.isDeviceConnected();

        if (!isDeviceConnected) return onReturn();

        if (isHolder) {
            const isHolderConnected = getIsHolderConnected();

            if (!isHolderConnected) return onReturn();
        }

        const isGamBackend = backendService.isGamBackend();
        const isDeviceInfoValid = isGamBackend
            ? await gamClientService.getGamDeviceInfo(assetId)
            : await uamClientService.getUamInfo(assetId);

        if (!isDeviceInfoValid) return onReturn();

        isDeviceConnected = pairingService.isDeviceConnected();
        if (!isDeviceConnected) return onReturn();

        if (isHolder) {
            const isHolderConnected = getIsHolderConnected();

            if (!isHolderConnected) return onReturn();
        }

        isSuccess = true;
        if (isHolder) {
            dispatch(setHolderInfoReceived(true));
        } else {
            dispatch(setDeviceInfoReceived(true));
        }
    }

    onReturn();
};

const getIsHolderConnected = () => {
    const state = getState();
    const {device} = makeSelectIotDeviceData()(state) || {};
    const {isHolderConnected} = device || {};

    return isHolderConnected;
};

const proceedConnectionChange = (status, assetId, isSBLMode, engine) => {
    const state = getState();
    const isHolder = makeSelectIsHolderByAssetId(assetId)(state);

    if (status === yapConnectionStatusTypes.CONNECTED) {
        const isHolderConnected = getIsHolderConnected();

        if (isHolder && !isHolderConnected) return;

        const sblMode = {is_device_in_sbl_mode: isSBLMode};
        const data = isHolder ? {holder: sblMode} : {device: sblMode};

        dispatch(updateIotDeviceData(data));

        if (connectivityService.isUsb()) {
            yapStateService.updateEngineData(isHolder, engine);
        }

        if (cmClientService.isCmClientConnected(isHolder)) {
            log.info(`uamClientService: connection of the device is changed. CONNECTED for isHolder ${isHolder}`);
            if (isHolder) {
                dispatch(setYapHolderConnected(true));
            } else {
                dispatch(setYapDeviceConnected(true));
            }
        }
    } else if (status === yapConnectionStatusTypes.DISCONNECTED) {
        log.info(`uamClientService: connection of the device is changed. DISCONNECTED for isHolder ${isHolder}`);
        if (isHolder) {
            dispatch(setYapHolderConnected(false));
        } else {
            dispatch(setYapDeviceConnected(false));
        }
    } else if (status === yapConnectionStatusTypes.INCORRECT_DEVICE) {
        log.info('End synchronization process. Incorrect Device.');
        dispatch(setIsWrongDevice());
        dispatch(setYapSyncInProgress(false, isHolder));
    }
};

const checkConnectivityStatus = async (isHolder) => {
    const assetId = makeSelectAssetIdByType(isHolder)(getState());
    const isGamBackend = backendService.isGamBackend();
    const isConnected = await cmClientService.isCmClientConnected(isHolder);

    if (!isConnected) return;

    const data = isGamBackend
        ? await new GamClient().callGet({
              methodName: gamMethods.CHECK_CONNECTIVITY_STATUS,
              requestConfig: {args: assetId},
              isNeedRedirectForOtherErrors: false,
          })
        : await new UamClient().callGet({
              methodName: uamMethods.CHECK_CONNECTIVITY_STATUS,
              requestConfig: {args: assetId},
              isNeedRedirectForOtherErrors: false,
          });

    if (!data) return;

    const {status, isSBLMode} = data;

    proceedConnectionChange(status, assetId, isSBLMode);
};

const forceReconnectToCm = async (isHolder, isForceDisconnect = true) => {
    if (isForceDisconnect) {
        cmClientService.disconnectCmClient(isHolder);
    }

    const state = getState();
    const isNeedReconnect = isHolder
        ? makeSelectIsYapHolderInfoReceived()(state)
        : makeSelectIsYapDeviceInfoReceived()(state);

    if (isNeedReconnect) {
        await syncAndSubscribeOnDevice(isHolder);
    }
};

export default {
    checkConnectivityStatus,
    forceReconnectToCm,
    initScpData,
    initSynchronizeFlow,
    proceedConnectionChange,
    proceedRefreshInfo,
    syncAndSubscribeOnDevice,
    synchronizeDevice, //used for tests. use initSynchronizeFlow instead
};
